import httpClient from './httpClient.js';

// 환경 변수에서 API URL 가져오기
const REGISTER_ENDPOINT = `/regmng`;
const ANALYTICS_ENDPOINT = `${REGISTER_ENDPOINT}/analytics`;
const MARKET_SETTING_ENDPOINT = `/setting/marketsetting`;

/**
 * 상품 조회수 통계를 가져옵니다
 * @param {Object} params - 조회 파라미터
 * @param {string} params.productId - 특정 상품 ID로 필터링
 * @param {number} params.days - 검색에 포함할 과거 일수 (최대 365)
 * @param {number} params.minViews - 최소 조회수 필터
 * @param {number} params.maxViews - 최대 조회수 필터
 * @param {string} params.sortOrder - 정렬 순서 (asc, desc)
 * @returns {Promise} API 응답
 */
export const getTrackingStats = async (params) => {
  try {
    const response = await httpClient.get(`${ANALYTICS_ENDPOINT}/tracking-stats`, {
      params: params
    });
    return response.data;
  } catch (error) {
    console.error('트래킹 통계 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 특정 상품의 날짜별 상세 조회수 데이터를 가져옵니다
 * @param {Object} params - 조회 파라미터
 * @param {string} params.productId - 조회할 상품 ID (필수)
 * @param {number} params.days - 검색에 포함할 과거 일수 (최대 365)
 * @returns {Promise} API 응답
 */
export const getTrackingDetails = async (params) => {
  try {
    const response = await httpClient.get(`${ANALYTICS_ENDPOINT}/tracking-details`, {
      params: params
    });
    return response.data;
  } catch (error) {
    console.error('트래킹 상세 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 등록 캘린더 집계를 조회합니다
 * @param {Object} params - 조회 파라미터
 * @param {string} [params.startDate] - 시작일 (YYYY-MM-DD)
 * @param {string} [params.endDate] - 종료일 (YYYY-MM-DD)
 * @returns {Promise} API 응답
 */
export const getRegisterCalendar = async (params = {}) => {
  try {
    const response = await httpClient.get(`${ANALYTICS_ENDPOINT}/calendar`, { params });
    return response.data;
  } catch (error) {
    console.error('등록 캘린더 집계 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 네이버 광고 대상 상품 ID 목록을 조회합니다.
 * @param {Object} params - 조회 파라미터
 * @param {number} [params.recentDays] - 최근 N일
 * @param {number} [params.marketNumber] - 네이버 마켓 번호
 * @param {number} [params.minPrice] - 최소 가격
 * @param {number} [params.maxPrice] - 최대 가격
 * @param {string} [params.groupCode] - 상품 그룹 코드
 * @returns {Promise} API 응답
 */
export const getNaverAds = async (params) => {
  try {
    const response = await httpClient.get(`${REGISTER_ENDPOINT}/naver-ads`, { params });
    return response.data;
  } catch (error) {
    console.error('네이버 광고 대상 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 네이버 광고 금지 대상 상품을 등록합니다.
 * POST /regmng/naver-ads/ban
 * @param {{smartstoreChannelProductNo: string[], option: 'n2n'|'basic'}} payload
 * @returns {Promise} API 응답
 */
export const banNaverAds = async (payload) => {
  try {
    const response = await httpClient.post(`${REGISTER_ENDPOINT}/naver-ads/ban`, payload);
    return response.data;
  } catch (error) {
    console.error('네이버 광고 금지 등록 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 재등록용 마켓 정보를 조회합니다
 * @param {string} platform - 플랫폼
 * @returns {Promise} API 응답
 */
export const getReRegisterMarkets = async (platform) => {
  try {
    const params = platform ? { platform } : {};
    const response = await httpClient.get(`${MARKET_SETTING_ENDPOINT}/markets`, { params });
    return response.data;
  } catch (error) {
    console.error('재등록 마켓 정보 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 트래픽 총량을 조회합니다
 * @param {Object} params - 조회 파라미터
 * @param {number} [params.days=30] - 조회 기간 (1-365일)
 * @returns {Promise} API 응답
 */
export const getTotalTraffic = async (params = {}) => {
  try {
    const response = await httpClient.get(`${ANALYTICS_ENDPOINT}/total-traffic`, { params });
    return response.data;
  } catch (error) {
    console.error('트래픽 총량 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * N2N 마켓별 일별 조회수를 조회합니다
 * @param {Object} params - 조회 파라미터
 * @param {number} [params.days=30] - 조회 기간 (1-365일)
 * @returns {Promise} API 응답
 */
export const getN2nMarketTraffic = async (params = {}) => {
  try {
    const response = await httpClient.get(`${ANALYTICS_ENDPOINT}/market-traffic`, { params });
    return response.data;
  } catch (error) {
    console.error('N2N 마켓별 조회수 조회 중 오류 발생:', error);
    throw error;
  }
};
